import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import { useNavigator } from '@repo/toolkit/use-navigator'
import AddressLink from '@repo/ui/address-link'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { BusTwoIcon, ChevronLeftIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useParams } from 'react-router-dom'

import { getCoachCompany } from '../../api/ols/coach-companies/get-coach-comapny'
import CountryFlag from '../../components/elements/country-flag'
import DisplayImageInfo from '../../components/elements/display-image-info'
import LabelWithText from '../../components/elements/label-with-text'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import { getOlPermissions } from '../../lib/constants/ol-permissions'
import { Paths } from '../../lib/constants/path'
import useAuth from '../../lib/hooks/use-auth'
import { useNativeCountryName } from '../../lib/hooks/use-native-country-name'
import useTranslate from '../../lib/hooks/use-translate'
import { outingDurationEnumMapping } from '../../lib/types/enums/cultural-outing/outing-duration.enum'
import { getCountryCodeFromPhoneNumber } from '../../lib/utils/functions/get-country-code-from-phone-number'
import { formatPhoneNumber } from '../../utils/functions/format-phone-number'
import NotFoundPage from '../common/not-found'
import { getOlName } from './center/utils/functions'
import { constructOlPath } from './views/utils'

const OlCoachCompaniesViewPage = () => {
  const nativeCountryName = useNativeCountryName()
  const { id, olId } = useParams<{ id: string; olId: string }>()
  const { t } = useTranslate()
  const { userRole } = useAuth()
  const { navigate, back } = useNavigator()
  const location = useLocation()

  const { data, isPending } = useQuery({
    queryKey: ['coach-companies', id, olId],
    queryFn: () => {
      if (!id || isNaN(+id) || !olId) return
      return getCoachCompany({ id: +id, olId: +olId })
    },
    enabled: !!id && !!olId
  })

  const permissions = getOlPermissions({ role: userRole, type: data?.ol.type })

  if (isPending) return <LoadingScreen />
  if (!data) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={getOlName(data.ol)} icon={BusTwoIcon} />
      <div className="h-4" />
      <Box>
        <Button
          type="button"
          label="Retour"
          icon={ChevronLeftIcon}
          variant="secondary"
          onClick={() =>
            (location.state as { from?: string } | null)?.from
              ? back()
              : navigate(constructOlPath(Paths.OL_COACH_COMPANIES_LIST_PAGE, data.ol.id), {
                  replace: true
                })
          }
          className="mb-4"
        />
        <Typography variant="h6" className="mb-4" color={tailwindColors.black.DEFAULT}>
          {data.name}
        </Typography>
        <div className="grid grid-cols-3  gap-4">
          <Box className="bg-white-hover rounded-lg p-4 border border-grey-light">
            <Typography color={tailwindColors.primary.DEFAULT} variant="text">
              Détail
            </Typography>
            <div className="h-3" />
            <div className="grid grid-cols-2 gap-4 ">
              <LabelWithText
                label="Numéro de téléphone"
                data={
                  data.phoneNumber ? (
                    <div className="flex gap-2">
                      <CountryFlag code={getCountryCodeFromPhoneNumber(data.phoneNumber)} />
                      <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                        {formatPhoneNumber(data.phoneNumber)}
                      </Typography>
                    </div>
                  ) : (
                    '-'
                  )
                }
              />
              <LabelWithText
                label="Numéro d'urgence"
                data={
                  data.emergencyNumber ? (
                    <div className="flex gap-2">
                      <CountryFlag code={getCountryCodeFromPhoneNumber(data.emergencyNumber)} />
                      <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                        {formatPhoneNumber(data.emergencyNumber)}
                      </Typography>
                    </div>
                  ) : (
                    '-'
                  )
                }
              />
            </div>
          </Box>

          <Box className="bg-white-hover rounded-lg p-4 col-span-2 border border-grey-light">
            <Typography color={tailwindColors.primary.DEFAULT} variant="text">
              Adresse
            </Typography>
            <div className="h-3" />
            <div className="grid grid-cols-5 ">
              <LabelWithText
                label="Adresse"
                data={
                  <AddressLink
                    address1={data.address1}
                    zipCode={data.zipCode}
                    city={data.city}
                    country={nativeCountryName(data.country)}
                  />
                }
              />

              <LabelWithText label="Complément d'adresse" data={valueRender(data.address2)} />
              <LabelWithText label="Ville" data={data.city} />
              <LabelWithText label="Code postal" data={data.zipCode} />
              <LabelWithText label="Pays" data={nativeCountryName(data.country)} />
            </div>
          </Box>
        </div>
        <div className="h-4" />

        {permissions['coach-company-full-view'] && (
          <div className="grid grid-cols-3 grid-rows-2 gap-4">
            <Box className="bg-white-hover rounded-lg p-4 row-span-2 border border-grey-light">
              <Typography color={tailwindColors.primary.DEFAULT} variant="text">
                Devis
              </Typography>
              <div className="h-3" />
              {data.quotations && data.quotations.length > 0 ? (
                data.quotations?.map((quotation) => (
                  <div className="mb-3" key={quotation.id}>
                    <DisplayImageInfo
                      key={quotation.id}
                      file={quotation.file}
                      imageSizeVariant="large"
                      informationSection={
                        <Badge
                          variant="white"
                          text={`Valid until : ${DateFormatter.DDMMYYYY(quotation.validUntil)}`}
                        />
                      }
                      label={quotation.name}
                    />
                  </div>
                ))
              ) : (
                <Typography variant="tag" color={tailwindColors.grey.light.active}>
                  Aucun devis disponible
                </Typography>
              )}
            </Box>
            <Box className="bg-white-hover rounded-lg p-4 col-span-2 border border-grey-light">
              <Typography color={tailwindColors.primary.DEFAULT} variant="text">
                Excursions
              </Typography>
              <div className="h-3" />
              {data.culturalOutings.length > 0 ? (
                data.culturalOutings.map((outing, index) => (
                  <div className="flex items-baseline gap-2" key={outing.id}>
                    <div className="flex gap-3">
                      <div className="rounded-full bg-secondary-light h-6 w-6 p-2 flex items-center justify-center">
                        <Typography
                          variant="label-semibold"
                          color={tailwindColors.secondary.DEFAULT}
                        >
                          {index + 1}
                        </Typography>
                      </div>
                    </div>
                    <div className="grid grid-cols-6 items-center mb-3" key={outing.id}>
                      <LabelWithText label="Destination" data={outing.activity.locationName} />

                      <LabelWithText label="Sièges" data={outing.numberOfSeats} />

                      <LabelWithText
                        label="Durées"
                        data={<Badge text={t(outingDurationEnumMapping[outing.duration])} />}
                      />
                      <LabelWithText
                        label="Coût"
                        data={
                          <Badge
                            variant="primary"
                            text={NumberFormatter.formatCurrency(outing.cost, outing.currency)}
                          />
                        }
                      />
                      <LabelWithText
                        label="Valable jusqu'au"
                        data={
                          <Badge
                            text={
                              outing.validUntil ? DateFormatter.DDMMYYYY(outing.validUntil) : '-'
                            }
                          />
                        }
                      />
                      <LabelWithText label="Commentaires" data={valueRender(outing.comment)} />
                    </div>
                  </div>
                ))
              ) : (
                <Typography variant="tag" color={tailwindColors.grey.light.active}>
                  Aucun excursion disponible
                </Typography>
              )}
            </Box>
            <Box className="bg-white-hover rounded-lg p-4 col-span-2 border border-grey-light">
              <Typography color={tailwindColors.primary.DEFAULT} variant="text">
                Transferts Arrivée / départ
              </Typography>
              <div className="h-3" />
              {data.arrivalAndDepartures.length > 0 ? (
                data.arrivalAndDepartures.map((arrivalAndDeparture, index) => (
                  <div className="grid grid-cols-5 items-center mb-3" key={arrivalAndDeparture.id}>
                    <div className="flex gap-3">
                      <div className="rounded-full bg-secondary-light h-6 w-6 p-2 flex items-center justify-center">
                        <Typography
                          variant="label-semibold"
                          color={tailwindColors.secondary.DEFAULT}
                        >
                          {index + 1}
                        </Typography>
                      </div>

                      <LabelWithText
                        label="De/Vers"
                        data={<AddressLink address1={arrivalAndDeparture.fromTo} />}
                      />
                    </div>
                    <LabelWithText label="Sièges" data={arrivalAndDeparture.numberOfSeats} />
                    <LabelWithText
                      label="Coût A+D"
                      data={
                        <Badge
                          variant="primary"
                          text={NumberFormatter.formatCurrency(
                            arrivalAndDeparture.cost,
                            arrivalAndDeparture.currency
                          )}
                        />
                      }
                    />
                    <LabelWithText
                      label="Valable jusqu'au"
                      data={
                        <Badge
                          text={
                            arrivalAndDeparture.validUntil
                              ? DateFormatter.DDMMYYYY(arrivalAndDeparture.validUntil)
                              : '-'
                          }
                        />
                      }
                    />
                    <LabelWithText
                      label="Commentaires"
                      data={valueRender(arrivalAndDeparture.comment)}
                    />
                  </div>
                ))
              ) : (
                <Typography variant="tag" color={tailwindColors.grey.light.active}>
                  Aucun excursion disponible
                </Typography>
              )}
            </Box>
          </div>
        )}

        {permissions['coach-company-full-view'] && (
          <>
            <div className="h-4" />
            <Box className="bg-white-hover rounded-lg p-4 col-span-2 border border-grey-light">
              <Typography color={tailwindColors.primary.DEFAULT} variant="text">
                Historique des centres
              </Typography>
              <div className="h-3" />
              <Typography variant="tag" color={tailwindColors.grey.light.active}>
                Aucun historique disponible
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </div>
  )
}

export default OlCoachCompaniesViewPage
